import { useAtomValue } from "jotai";
import { useEffect } from "react";

import { workosAuthAtom } from "~/atoms";
import { isWorkOSAuth } from "~/lib/authProvider";
import { SKEW_MS, ensureFreshSession } from "~/lib/refreshCoordinator";
import { Env } from "~/utils/Env";

/**
 * Proactive silent refresh (V3 — INS-1394, affordances N7 + N8). On the
 * WorkOS auth path, refresh the session BEFORE a fetch can 401, making the
 * short 15-min access-token TTL invisible to users:
 *
 *   - N7 (visibilitychange): a backgrounded tab returning to the foreground
 *     is likely stale, so refresh on wake.
 *   - N8 (expiry timer): fires ~SKEW_MS before the access token lapses and
 *     re-arms after every refresh (expiresAt changes → effect re-runs).
 *
 * Both funnel through ensureFreshSession({ confidence: 'known' }) as
 * *proactive* (non-reactive) calls, so authGen is NOT bumped — native image
 * caching keeps working. Only the reactive 401 path in createAxiosRequest
 * bumps authGen.
 *
 * Inert unless the session is actually hydrated on the real WorkOS path:
 *   - !isWorkOSAuth()  → legacy Zitadel, nothing to refresh.
 *   - VITE_MOCK        → no real authkit-ui session (keeps Playwright green).
 *   - csrfToken null   → not yet hydrated (or logged out); a blind proactive
 *                        refresh here has no session to bind to and would
 *                        bounce to login. Reactive recovery owns that case.
 *
 * ensureFreshSession's single-flight + skew check debounce on their own, so
 * no extra storm guard is needed for N7/N8 (the <img onError> storm risk
 * belongs to V4's 'suspected' trigger, not here).
 */
export function useSilentRefresh(): void {
  const { csrfToken, expiresAt } = useAtomValue(workosAuthAtom);
  const isActive =
    isWorkOSAuth() && Env.VITE_MOCK !== "true" && csrfToken != null;

  // N7 — refresh when a backgrounded tab wakes up. One listener for the
  // active lifetime; ignores the 'hidden' transition.
  useEffect(() => {
    if (!isActive) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void ensureFreshSession({ confidence: "known" });
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [isActive]);

  // N8 — fire ~SKEW_MS before expiry. Re-arms whenever expiresAt changes
  // (after every refresh). ensureFreshSession re-checks the skew and
  // single-flights, so an early or duplicate fire is harmless.
  useEffect(() => {
    if (!isActive || expiresAt == null) return;
    const delay = Math.max(0, expiresAt - SKEW_MS - Date.now());
    const timer = window.setTimeout(() => {
      void ensureFreshSession({ confidence: "known" });
    }, delay);
    return () => window.clearTimeout(timer);
  }, [isActive, expiresAt]);
}
